import { Collapse, Typography } from "antd";
import type { CollapseProps } from "antd";

const { Title, Paragraph, Text } = Typography;

const faqItems: CollapseProps["items"] = [
  {
    key: "sell-esim",
    label: "How do I sell an eSIM plan?",
    children: (
      <div className="help-support__answer">
        <ol className="help-support__list">
          <li>
            Open <Text strong>Plans</Text> from the sidebar and pick a plan.
          </li>
          <li>
            Click <Text strong>Sell Package</Text> and confirm the sale.
          </li>
          <li>Share the generated QR code with the customer.</li>
        </ol>
        <Paragraph className="help-support__note">
          You can resend the QR any time from <Text strong>Transactions</Text> &gt;{" "}
          <Text strong>Debit (Sales)</Text> using Share eSIM.
        </Paragraph>
      </div>
    ),
  },
  {
    key: "topup-esim",
    label: "How do I top up an existing eSIM?",
    children: (
      <div className="help-support__answer">
        <ol className="help-support__list">
          <li>
            Open <Text strong>Top up</Text>.
          </li>
          <li>Search by ICCID, subscriber ID, or customer email.</li>
          <li>Select a plan and confirm.</li>
        </ol>
        <Paragraph className="help-support__note">
          QR code emails are sent from <Text strong>Share eSIM</Text> after the sale.
        </Paragraph>
      </div>
    ),
  },
  {
    key: "sim-packages",
    label: "What is the difference between Packages and Top up for SIM inventory?",
    children: (
      <div className="help-support__answer">
        <Paragraph>
          <Text strong>Packages</Text> is for SIMs in stock that have not been sold.{" "}
          <Text strong>Top up</Text> is for SIMs already sold and active.
        </Paragraph>
        <Paragraph className="help-support__note">
          You can move a SIM back to stock from the SIM Packages screen if needed.
        </Paragraph>
      </div>
    ),
  },
  {
    key: "transactions",
    label: "Where can I view my balance and transaction history?",
    children: (
      <div className="help-support__answer">
        <Paragraph>
          Go to <Text strong>Transactions</Text> to see live balance, credits, and
          debits.
        </Paragraph>
        <ul className="help-support__list">
          <li>
            <Text strong>Balance</Text> shows current credit.
          </li>
          <li>
            <Text strong>Credit (Topups)</Text> lists added funds.
          </li>
          <li>
            <Text strong>Debit (Sales)</Text> lists sales and allows CSV export.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: "share-esim",
    label: "How do I resend or print an eSIM QR code?",
    children: (
      <div className="help-support__answer">
        <Paragraph>
          In <Text strong>Transactions</Text> &gt; <Text strong>Debit (Sales)</Text>,
          click <Text strong>Share eSIM</Text> for the sale to email, print, or copy
          the activation details.
        </Paragraph>
      </div>
    ),
  },
  {
    key: "client-details",
    label: "Where can I find activation details for a client?",
    children: (
      <div className="help-support__answer">
        <Paragraph>
          Open <Text strong>My Clients</Text> to view ICCID, plan, activation, and
          expiry details. Use the row details to copy the SM-DP+ server and
          activation code.
        </Paragraph>
      </div>
    ),
  },
  {
    key: "account-settings",
    label: "How do I update business info, notifications, or my password?",
    children: (
      <div className="help-support__answer">
        <ul className="help-support__list">
          <li>
            Update business info in <Text strong>Account &gt; Business Info</Text>.
          </li>
          <li>
            Manage notifications in <Text strong>Account &gt; Notifications</Text>.
          </li>
          <li>Change your password from the profile menu in the top bar.</li>
        </ul>
      </div>
    ),
  },
  {
    key: "activation-issues",
    label: "What should I do if a customer cannot install the eSIM?",
    children: (
      <div className="help-support__answer">
        <ul className="help-support__list">
          <li>Confirm the device is eSIM compatible and online.</li>
          <li>Try manual activation using the SM-DP+ and activation code.</li>
          <li>
            If it still fails, contact Teltrip support with the error and sale
            details.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: "balance-topups",
    label: "How do balance topups work?",
    children: (
      <div className="help-support__answer">
        <Paragraph>
          Balance credits are applied by the Teltrip team. When posted, they appear
          under <Text strong>Credit (Topups)</Text> in Transactions.
        </Paragraph>
        <Paragraph className="help-support__note">
          Reach out to your Teltrip representative to request additional credit.
        </Paragraph>
      </div>
    ),
  },
];

export default function HelpSupportPage() {
  return (
    <div className="help-support">
      <div className="help-support__header">
        <div className="help-support__eyebrow">Teltrip Reseller Program</div>
        <Title level={2} className="help-support__title">
          Help & Support
        </Title>
        <Paragraph className="help-support__subtitle">
          Quick answers for selling, topups, transaction history, and account
          settings.
        </Paragraph>
      </div>

      <div className="help-support__content">
        <div className="help-support__panel">
          <Title level={4} className="help-support__section-title">
            Frequently asked questions
          </Title>
          <Collapse className="help-support__collapse" items={faqItems} accordion />
        </div>

        <div className="help-support__panel">
          <Title level={4} className="help-support__section-title">
            Contact support
          </Title>
          <Paragraph className="help-support__support-text">
            If you need help beyond the FAQ, email{" "}
            <Text strong>b2b@teltrip.com</Text> and include the details below for faster
            resolution.
          </Paragraph>
          <ul className="help-support__support-list">
            <li>Sale ID or subscriber ID</li>
            <li>ICCID or customer email</li>
            <li>Plan name and purchase date</li>
            <li>Device model and OS version (activation issues)</li>
            <li>Screenshot or exact error message</li>
          </ul>
          <Paragraph className="help-support__support-note">
            For balance topups, include the requested amount and your preferred
            reference.
          </Paragraph>
        </div>
      </div>
    </div>
  );
}
